import React, { useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { loadRazorpayScript } from "../lib/razorpay";

const BOX_TIERS = [
  { boxes: 1, tag: "Starter", desc: "1 box on the brand wall", perks: ["Verified brand page", "2 AI SEO articles / month", "Client portal & stats", "Google + AI search markup"] },
  { boxes: 3, tag: "Growth", popular: true, desc: "3 boxes = bigger card, higher placement", perks: ["Everything in Starter", "FEATURED bigger card", "Top placement on the wall", "6 AI SEO articles / month"] },
  { boxes: 5, tag: "Dominate", desc: "5 boxes = maximum visibility", perks: ["Everything in Growth", "Top-of-wall priority", "10 AI SEO articles / month", "Priority support"] },
];

export default function BbiPricing() {
  const [sel, setSel] = useState(1);
  const [form, setForm] = useState({ brand_name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const monthly = sel * 999;

  const pay = async () => {
    if (form.brand_name.trim().length < 2) return toast.error("Enter your brand name");
    if (!form.email.includes("@")) return toast.error("Enter a valid email");
    if (form.phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid 10-digit phone");
    setBusy(true);
    try {
      const { data } = await api.post("/public/bbi/create-subscription", { ...form, boxes: sel });
      await loadRazorpayScript();
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "BrandsByIndia",
        description: data.label,
        prefill: { name: form.brand_name, email: form.email, contact: form.phone },
        theme: { color: "#6366F1" },
        handler: async (resp) => {
          try {
            const v = await api.post("/public/bbi/verify-subscription", resp);
            toast.success(`Auto-Pay active! ₹${v.data.monthly_amount_inr}/month. Check your email — login details arrive within 24 hours.`);
          } catch (e) {
            toast.error(errMsg(e, "Verification failed — contact info@ecomcatalog.ai"));
          }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e, "Could not start payment"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="pricing" className="pb-24 pt-10" data-testid="bbi-pricing">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <h2 className="font-mkt-display text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
          Simple pricing: <span className="mkt-gradient-text-vivid">₹999 per box / month</span> — including GST
        </h2>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl leading-relaxed">
          One box = one spot on the BrandsByIndia wall. <span className="text-white font-semibold">More boxes = bigger card,
          higher placement and more AI articles = more visibility.</span> ₹999 per box (GST included),
          auto-deducted monthly via Razorpay Auto-Pay — cancel anytime.
        </p>

        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {BOX_TIERS.map((t) => (
            <button
              key={t.boxes}
              onClick={() => setSel(t.boxes)}
              data-testid={`bbi-tier-${t.boxes}`}
              className={`text-left rounded-2xl p-6 border transition-all relative ${
                sel === t.boxes ? "border-indigo-400 bg-indigo-500/10" : "border-white/10 bg-white/[0.03] hover:border-white/25"
              }`}
            >
              {t.popular && (
                <span className="absolute -top-2.5 left-5 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-[10px] font-bold uppercase tracking-wider" style={{ color: "#FFF" }}>
                  Most Popular
                </span>
              )}
              <div className="text-[11px] uppercase tracking-wider text-indigo-400 font-bold">{t.tag}</div>
              <div className="text-white text-2xl font-semibold mt-2">{t.boxes} Box{t.boxes > 1 ? "es" : ""}</div>
              <div className="text-sm text-slate-400 mt-1">₹{(t.boxes * 999).toLocaleString("en-IN")}/mo <span className="text-xs">incl. GST</span></div>
              <div className="text-xs text-slate-500 mt-1">{t.desc}</div>
              <ul className="mt-4 space-y-1.5">
                {t.perks.map((p) => (
                  <li key={p} className="text-xs text-slate-300 flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-[1px]" /> {p}
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        <div className="mt-8 bg-white/[0.03] border border-white/10 rounded-2xl p-6">
          <div className="text-white font-semibold text-sm mb-4">
            Start Auto-Pay — {sel} box{sel > 1 ? "es" : ""} · ₹{monthly.toLocaleString("en-IN")}/month (incl. GST)
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <input data-testid="bbi-pay-brand" placeholder="Brand name" value={form.brand_name}
              onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
              className="bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60" />
            <input data-testid="bbi-pay-email" placeholder="Email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60" />
            <input data-testid="bbi-pay-phone" placeholder="Phone (10 digits)" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60" />
          </div>
          <button data-testid="bbi-pay-submit" onClick={pay} disabled={busy}
            className="mt-4 px-8 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-sm font-semibold disabled:opacity-60"
            style={{ color: "#FFFFFF" }}>
            {busy ? "Starting…" : `Pay & Activate Auto-Pay — ₹${monthly.toLocaleString("en-IN")}/mo`}
          </button>
          <p className="text-[11px] text-slate-500 mt-3">
            Secure payment by Razorpay. Monthly amount auto-deducts from your account; your page + login are set up within 24 hours. Cancel anytime by emailing info@ecomcatalog.ai.
          </p>
        </div>
      </div>
    </section>
  );
}
